/**
 * MH-12 Biên tập bản tin (mục 5.2 đặc tả giao diện, Bảng 5.2) — Kho nội dung cá nhân dùng chung
 * cho cụm loa và bảng tin điện tử (hợp đồng `docs/bang-tin/HOP-DONG-A0-GOP-BAN-TIN.md`, 29/10/2026).
 *
 * Ba bước theo ảnh mẫu tỉnh: "1 Tạo bản tin → 2 Tải tệp lên → 3 Hoàn thành".
 *   - Bước 1: Tên bản tin; Quy trình; Lĩnh vực cấp 1 | cấp 2; Mức độ ưu tiên | Nguồn tin; Loại bản
 *     tin | Vùng phát thiết bị; Địa bàn chia sẻ nội dung (cấp sở, cấp xã). Ô "Loại bản tin" (Âm
 *     thanh / Hình ảnh / Video) chọn được khi tạo mới, khoá sau khi bản tin đã tạo. "Vùng phát
 *     thiết bị" (`'0'`–`'3'`) chỉ bật khi Hình ảnh/Video. Nút "Tạo bản tin" gọi `POST
 *     /api/v1/bulletins` (Âm thanh → loại `3`, Hình ảnh → `2`, Video → `4`); khi sửa thì "Cập nhật"
 *     gọi `PUT` (giữ nguyên mã loại gốc `1`/`3` của bản tin âm thanh).
 *   - Bước 2: Âm thanh giữ nguyên cấu tạo âm thanh (danh sách đoạn: tải tệp, tổng hợp giọng nói,
 *     đổi thứ tự bằng kéo thả VÀ bằng bàn phím, xóa đoạn); Hình ảnh/Video tải MỘT tệp qua `POST
 *     /bulletins/:id/file` (kèm thời lượng `HH:mm:ss` cho video), xem trước qua `file-url`.
 *   - Bước 3: màn hình hoàn thành, trình phê duyệt ngay được.
 *
 * Giữ các yêu cầu bắt buộc cũ: tự lưu nháp 30 giây khi đã có bản tin, cảnh báo rời trang khi còn
 * thay đổi chưa lưu, nút gửi duyệt vô hiệu khi chưa có nội dung (âm thanh: chưa có đoạn; hình
 * ảnh/video: chưa có tệp). QĐ-28: `dia_ban_tao` là MỘT mã địa bàn, hiển thị chỉ đọc.
 *
 * `/ban-tin/moi` KHÔNG tạo bản nháp ngay nữa (người dùng phải chọn loại trước): bản tin chỉ được
 * tạo khi bấm "Tạo bản tin" ở bước 1. Quy trình và Lĩnh vực cấp 2 API chưa nhận → khoá, gắn
 * "Chưa có API".
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viBienTapNoiDung as t } from '../ngon-ngu/vi-bien-tap';
import { viBangTin } from '../ngon-ngu/vi-bang-tin';
import {
  banTinApi,
  DINH_DANG_TEP,
  DS_NHOM_LOAI_BAN_TIN,
  DS_VUNG_PHAT,
  nhomLoaiBanTin,
  tepDungLoaiBanTin,
  type DongBanTin,
  type DongDoanAmThanh,
  type NhomLoaiBanTin,
  type TienTrinhTaiLen,
  type VungPhat,
} from '../api/ban-tin-api';
import { khoAmThanhApi } from '../api/kho-am-thanh-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { KhungXuong, TrangThaiLoi } from '../thanh-phan/TrangThaiManHinh';
import { TrinhPhatAmThanh } from '../thanh-phan/TrinhPhatAmThanh';
import { NhanChuaCoApi } from '../thanh-phan/MauGiaoDien';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BieuTuongNoiDung } from './ChonLoaiNoiDung';
import { TagTrangThaiBanTin } from './MH11DanhSachBanTin';
import { dungDanhSachDiaBan, type TaiDiaBan } from './MH17LichTuan';
import { kieuTep, TrinhXemTep } from './BangTinChung';
import { thietBiApi } from '../api/thiet-bi-api';
import { ChonNhieuDiaBan } from '../thanh-phan/ChonNhieuDiaBan';
import './bien-tap.css';
import './bang-tin.css';

const CHU_KY_TU_LUU_MS = 30_000;
const DINH_DANG_CHO_PHEP = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];
const KHUON_THOI_LUONG = /^\d{2}:[0-5]\d:[0-5]\d$/;

type ApiTepBanTin = Pick<typeof banTinApi, 'taiTep' | 'linkTep'>;

export interface MH12Props {
  api?: Pick<typeof banTinApi, 'chiTiet' | 'tao' | 'sua' | 'trinhDuyet' | 'doanAmThanh'> &
    Partial<ApiTepBanTin>;
  khoApi?: Pick<typeof khoAmThanhApi, 'taiLen'>;
}

/* ------------------------ Thành phần dùng chung cho MH-12/13 ------------------------ */

/** Thanh bước "1 Tạo bản tin — 2 Tải tệp lên — 3 Hoàn thành" của ảnh mẫu tỉnh. */
export function BuocSoanBanTin({ buoc }: { buoc: 1 | 2 | 3 }): ReactNode {
  const ds = [t.soan.buocTao, t.soan.buocTaiTep, t.soan.buocHoanThanh];
  return (
    <ol className="thanh-buoc" data-vung="thanh-buoc">
      {ds.map((nhan, i) => (
        <li
          key={nhan}
          className={i + 1 < buoc ? 'nd-buoc-xong' : undefined}
          aria-current={i + 1 === buoc ? 'step' : undefined}
        >
          {nhan}
        </li>
      ))}
    </ol>
  );
}

/** Trường biểu mẫu nhãn TRÊN control, control lớn 40px (theo `.fi` của mẫu). */
export function TruongSoan({
  nhan,
  htmlFor,
  batBuoc,
  chuaCoApi,
  children,
}: {
  nhan: string;
  htmlFor?: string;
  batBuoc?: boolean;
  chuaCoApi?: boolean;
  children: ReactNode;
}): ReactNode {
  const phan = (
    <>
      {nhan}
      {batBuoc ? (
        <span className="nd-bat-buoc" aria-hidden="true">
          *
        </span>
      ) : null}
      {chuaCoApi ? <NhanChuaCoApi /> : null}
    </>
  );
  return (
    <div className="nd-truong">
      {htmlFor ? <label htmlFor={htmlFor}>{phan}</label> : <span className="nd-truong__nhan">{phan}</span>}
      {children}
    </div>
  );
}

const taiTinh: TaiDiaBan = (signal) => thietBiApi.cayDiaBan({ cap: 'tinh' }, signal);

/**
 * Địa bàn chia sẻ nội dung: cấp sở = tỉnh (một hộp kiểm), cấp xã = các xã thuộc tỉnh (chọn
 * nhiều). Tích cấp sở = chia sẻ tới TOÀN BỘ xã, phường của tỉnh: chỉ lưu mã tỉnh, ô cấp xã khoá. Lưu vào `diaBanChiaSe` của bản tin; bản tin đã duyệt hiện trong Kho thông tin nguồn
 * của địa bàn được chia sẻ. Vùng phát thiết bị chỉ dùng cho bảng tin điện tử (MH-25).
 */
export function TruongDiaBanChiaSe({
  tienTo,
  giaTri,
  onDoi,
}: {
  tienTo: string;
  giaTri: string[];
  onDoi: (ds: string[]) => void;
}): ReactNode {
  const dsTinh = dungDanhSachDiaBan(taiTinh);
  const dsXa = dungDanhSachDiaBan();
  const tinh = dsTinh[0];
  const coTinh = tinh ? giaTri.includes(tinh.maDinhDanh) : false;
  const xaDaChon = giaTri.filter((m) => m !== tinh?.maDinhDanh);
  return (
    <TruongSoan nhan={t.soan.diaBanChiaSe}>
      <span className="nd-truong__phu">{t.soan.capSo}</span>
      <label className="nd-hop-kiem" htmlFor={`${tienTo}-chia-se-so`}>
        <input
          id={`${tienTo}-chia-se-so`}
          type="checkbox"
          disabled={!tinh}
          checked={coTinh}
          onChange={() => tinh && onDoi(coTinh ? [] : [tinh.maDinhDanh])}
        />
        {dienVao(t.soan.chiaSeTinh, { ten: tinh?.ten ?? t.soan.chonDiaBan })}
      </label>
      <span className="nd-truong__phu">{t.soan.capXa}</span>
      <ChonNhieuDiaBan
        id={`${tienTo}-chia-se-xa`}
        nhan={`${t.soan.diaBanChiaSe} — ${t.soan.capXa}`}
        ds={dsXa.map((x) => ({
          ma: x.maDinhDanh,
          ten: x.ten.charAt(0).toLocaleUpperCase('vi') + x.ten.slice(1),
        }))}
        giaTri={coTinh ? [] : xaDaChon}
        onDoi={onDoi}
        khoa={coTinh}
        goiY={coTinh ? t.soan.toanBoXa : t.soan.chonXa}
        nhanDaChon={(so) => dienVao(t.soan.daChonXa, { so })}
        nhanTatCa={t.soan.chonTatCa}
        nhanBoChon={t.soan.boChonTatCa}
        goiYTim={t.soan.timXa}
      />
      <span className="truong__mo-ta">{t.soan.ghiChuChiaSe}</span>
    </TruongSoan>
  );
}

/** Bước 2 — màn hình hoàn thành. */
export function HoanThanhBanTin({
  laTaoMoi,
  ten,
  trangThai,
  banTinId,
  trinhDuoc,
  dangTrinh,
  loiTrinh,
  ghiChu,
  onTrinh,
}: {
  laTaoMoi: boolean;
  ten: string;
  trangThai: DongBanTin['trangThai'];
  banTinId: string;
  trinhDuoc: boolean;
  dangTrinh: boolean;
  loiTrinh: string | null;
  ghiChu?: string;
  onTrinh: () => void;
}): ReactNode {
  return (
    <div className="nd-hoan-thanh" data-vung="hoan-thanh">
      <div className="nd-hoan-thanh__dau-tich">
        <BieuTuongNoiDung ten="tich" co={36} net={2.4} />
      </div>
      <h2>{laTaoMoi ? t.soan.daTao : t.soan.daCapNhat}</h2>
      <p>
        {dienVao(t.soan.moTaHoanThanh, { ten, trangThai: vi.banTin.trangThaiNhan[trangThai] ?? trangThai })}{' '}
        <TagTrangThaiBanTin trangThai={trangThai} />
      </p>
      {ghiChu ? <p className="bien-tap__ghi-chu">{ghiChu}</p> : null}
      {loiTrinh ? (
        <p role="alert" className="nd-loi">
          {loiTrinh}
        </p>
      ) : null}
      <div className="nd-chan-form">
        <Link to="/ban-tin" className="nut">
          {t.soan.veKho}
        </Link>
        {trangThai === 'tao_moi' ? (
          <button
            type="button"
            className="nut nut--chinh"
            disabled={!trinhDuoc || dangTrinh}
            onClick={onTrinh}
          >
            {t.soan.trinhNgay}
          </button>
        ) : null}
        <Link to={`/ban-tin/${banTinId}/sua`} className="nut">
          {t.soan.moBienTap}
        </Link>
        <Link to="/ban-tin/chon-loai" className="nut">
          {t.soan.taoKhac}
        </Link>
      </div>
    </div>
  );
}

function gioHienTai(): string {
  const d = new Date();
  const hai = (n: number): string => String(n).padStart(2, '0');
  return `${hai(d.getHours())}:${hai(d.getMinutes())}:${hai(d.getSeconds())}`;
}

/** Mã loại gửi máy chủ theo ô "Loại bản tin"; Âm thanh giữ mã gốc `1`/`3` của bản tin đã có. */
function maLoaiTheoNhom(nhom: NhomLoaiBanTin, maGoc: string | undefined): string {
  if (nhom === 'hinh-anh') return '2';
  if (nhom === 'video') return '4';
  return maGoc && maGoc !== '2' && maGoc !== '4' ? maGoc : '3';
}

/* ------------------------------ Bước 2: tải tệp hình ảnh / video ------------------------------ */

function KhuTaiTep({
  id,
  loai,
  tieuDe,
  tenTep,
  api,
  onDaTai,
}: {
  id: string;
  loai: '2' | '4';
  tieuDe: string;
  tenTep: string | null;
  api: ApiTepBanTin;
  onDaTai: (ten: string) => void;
}): ReactNode {
  const [tep, datTep] = useState<File | null>(null);
  const [thoiLuong, datThoiLuong] = useState('');
  const [loi, datLoi] = useState<string | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [dangTai, datDangTai] = useState(false);
  const [tienTrinh, datTienTrinh] = useState<TienTrinhTaiLen | null>(null);
  const [url, datUrl] = useState<string | null>(null);

  const layLinkXem = useCallback(
    (signal?: AbortSignal): void => {
      api
        .linkTep(id, signal)
        .then((kq) => {
          if (!signal?.aborted) datUrl(kq.url || null);
        })
        .catch(() => {
          if (!signal?.aborted) datLoi(t.tep.khongXemDuoc);
        });
    },
    [api, id],
  );

  // Bản tin đã có tệp (mở lại để sửa): lấy link xem trước ngay khi vào bước 2.
  const daCoTepBanDau = useRef(tenTep !== null);
  useEffect(() => {
    if (!daCoTepBanDau.current) return undefined;
    const boHuy = new AbortController();
    layLinkXem(boHuy.signal);
    return () => boHuy.abort();
  }, [layLinkXem]);

  const dinhDang = t.tep.dinhDang[loai] ?? '';

  const chonTep = (f: File | null): void => {
    datThongBao(null);
    if (f && !tepDungLoaiBanTin(f, loai)) {
      datTep(null);
      datLoi(dienVao(t.tep.saiLoai, { ten: f.name, loai: t.loaiBanTinNhan[loai], dinhDang }));
      return;
    }
    datLoi(null);
    datTep(f);
  };

  const taiLen = async (): Promise<void> => {
    if (!tep) return;
    const tl = thoiLuong.trim();
    if (loai === '4' && tl && !KHUON_THOI_LUONG.test(tl)) {
      datLoi(t.tep.thoiLuongSai);
      return;
    }
    datDangTai(true);
    datLoi(null);
    datTienTrinh({ daGui: 0, tong: tep.size });
    try {
      const kq = await api.taiTep(id, tep, loai === '4' && tl ? tl : undefined, datTienTrinh);
      const ten = kq?.tep?.ten ?? tep.name;
      onDaTai(ten);
      datThongBao(dienVao(t.tep.daTaiLen, { ten }));
      datTep(null);
      layLinkXem();
    } catch (e: unknown) {
      datLoi(dienVao(t.tep.loiTaiLen, { loi: e instanceof Error ? e.message : '' }));
    } finally {
      datDangTai(false);
      datTienTrinh(null);
    }
  };

  const dd = DINH_DANG_TEP[loai];
  const MB = 1024 * 1024;
  const phanTram = tienTrinh && tienTrinh.tong > 0 ? Math.floor((tienTrinh.daGui / tienTrinh.tong) * 100) : 0;
  return (
    <section className="nd-panel" aria-label={t.soan.buocTaiTep} data-vung="tai-tep-ban-tin">
      <TruongSoan nhan={t.tep.nhan} htmlFor="bt-tep-ban-tin" batBuoc>
        <input
          id="bt-tep-ban-tin"
          type="file"
          accept={[...dd.mime, ...dd.duoi.map((d) => `.${d}`)].join(',')}
          onChange={(e) => chonTep(e.target.files?.[0] ?? null)}
        />
        <span className="truong__mo-ta">{dienVao(t.tep.dinhDangNhan, { dinhDang })}</span>
        {tenTep ? (
          <span className="truong__mo-ta" data-vung="tep-hien-tai">
            {dienVao(t.tep.tepHienTai, { ten: tenTep })}
          </span>
        ) : null}
      </TruongSoan>
      {loai === '4' ? (
        <TruongSoan nhan={t.tep.thoiLuong} htmlFor="bt-thoi-luong">
          <input
            id="bt-thoi-luong"
            placeholder="00:00:00"
            value={thoiLuong}
            onChange={(e) => datThoiLuong(e.target.value)}
          />
          <span className="truong__mo-ta">{t.tep.thoiLuongGoiY}</span>
        </TruongSoan>
      ) : null}
      <div>
        <button
          type="button"
          className="nut nut--chinh"
          data-hanh-dong="tai-len"
          disabled={!tep || dangTai}
          onClick={() => void taiLen()}
        >
          {dangTai ? t.tep.dangTaiLen : t.tep.taiLen}
        </button>
      </div>
      {dangTai && tienTrinh ? (
        <div className="nd-tien-trinh" data-vung="tien-trinh-tai-tep">
          <progress max={100} value={phanTram} aria-label={t.tep.tienTrinhNhan} />
          <span role="status">
            {phanTram >= 100
              ? t.tep.dangXuLy
              : dienVao(t.tep.tienTrinh, {
                  pt: phanTram,
                  daGui: (tienTrinh.daGui / MB).toFixed(1),
                  tong: (tienTrinh.tong / MB).toFixed(1),
                })}
          </span>
        </div>
      ) : null}
      {loi ? (
        <p role="alert" className="nd-loi" data-vung="loi-tai-tep">
          {loi}
        </p>
      ) : null}
      {thongBao ? (
        <p role="status" data-vung="thong-bao-tai-tep">
          {thongBao}
        </p>
      ) : null}
      {url ? (
        <div className="bt-xem-truoc" data-vung="xem-truoc">
          <h2 className="nd-tieu-de-phu">{t.tep.xemTruoc}</h2>
          <TrinhXemTep url={url} kieu={kieuTep(loai, url)} nhan={tieuDe} />
        </div>
      ) : null}
    </section>
  );
}

/* ------------------------------------ Màn hình ------------------------------------ */

export function MH12BienTapBanTin({ api, khoApi }: MH12Props = {}): ReactNode {
  const goi = api ?? banTinApi;
  const goiTaiTep = api?.taiTep ?? banTinApi.taiTep;
  const goiLinkTep = api?.linkTep ?? banTinApi.linkTep;
  const goiTep = useRef<ApiTepBanTin>({ taiTep: goiTaiTep, linkTep: goiLinkTep }).current;
  const khoGoi = khoApi ?? khoAmThanhApi;
  const { id: idDuongDan } = useParams<{ id: string }>();
  const dieuHuong = useNavigate();
  const phien = dungPhien();
  const [thamSoUrl] = useSearchParams();
  const loaiNoiDung = thamSoUrl.get('loai');
  const laTaoMoi = !idDuongDan || thamSoUrl.get('moi') === '1';

  /** uuid bản tin vừa tạo ở bước 1 (khi mở `/ban-tin/moi`). */
  const [idThat, datIdThat] = useState<string | null>(null);
  const id = idDuongDan ?? idThat;

  const banTin = dungTaiDuLieu<DongBanTin>({
    tai: (signal) => goi.chiTiet(id as string, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id],
    tat: !id,
  });

  /* --------------------------------- Trường biểu mẫu -------------------------------- */
  const [tieuDe, datTieuDe] = useState('');
  const [loaiLinhVuc, datLoaiLinhVuc] = useState('1');
  const [mucDoUuTien, datMucDoUuTien] = useState('2');
  const [nhomLoai, datNhomLoai] = useState<NhomLoaiBanTin>('am-thanh');
  const [vungPhat, datVungPhat] = useState<VungPhat>('0');
  const [diaBanChiaSe, datDiaBanChiaSe] = useState<string[]>([]);
  const [diaBanTao, datDiaBanTao] = useState(phien.nguonIdHienTai ?? '');
  const [tenDiaBanTao, datTenDiaBanTao] = useState<string | null>(null);
  const [noiDungTomTat, datNoiDungTomTat] = useState('');
  const [tenTep, datTenTep] = useState<string | null>(null);
  const [coThayDoi, datCoThayDoi] = useState(false);
  const [luuLucCuoi, datLuuLucCuoi] = useState<string | null>(null);
  const [dangLuu, datDangLuu] = useState(false);
  const [dangGuiDuyet, datDangGuiDuyet] = useState(false);
  const [thongBaoDoan, datThongBaoDoan] = useState<string | null>(null);
  const daNapRef = useRef(false);
  // Biểu mẫu chỉ hiện sau khi đã nạp chi tiết vào các ô (tránh bấm lưu khi ô còn rỗng).
  const [daNap, datDaNap] = useState(false);

  useEffect(() => {
    if (!banTin.duLieu || daNapRef.current) return;
    daNapRef.current = true;
    const d = banTin.duLieu;
    datTieuDe(d.tieuDe);
    datLoaiLinhVuc(d.loaiLinhVuc);
    datMucDoUuTien(d.mucDoUuTien);
    datNhomLoai(nhomLoaiBanTin(d.loaiBanTin) ?? 'am-thanh');
    datVungPhat(
      (DS_VUNG_PHAT as readonly string[]).includes(d.vungPhat ?? '') ? (d.vungPhat as VungPhat) : '0',
    );
    datDiaBanChiaSe(d.diaBanChiaSe ?? []);
    datDiaBanTao(d.diaBanTao);
    datTenDiaBanTao(d.tenDiaBanTao);
    datNoiDungTomTat(d.noiDungTomTat);
    datTenTep(d.tep?.ten ?? null);
    datDaNap(true);
  }, [banTin.duLieu]);

  const laAnhVideo = nhomLoai === 'hinh-anh' || nhomLoai === 'video';
  const maLoai = maLoaiTheoNhom(nhomLoai, banTin.duLieu?.loaiBanTin);
  // Loại bản tin chỉ chọn được trước khi bản tin được tạo.
  const choChonLoai = !id;

  const doan = dungTaiDuLieu<DongDoanAmThanh[]>({
    tai: (signal) => goi.doanAmThanh.danhSach(id as string, signal),
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [id, laAnhVideo],
    tat: !id || laAnhVideo,
  });

  /** Thân gửi máy chủ — PUT thay THẾ toàn bộ bản ghi nên gửi đủ trường bắt buộc. */
  const thanGui = useCallback(
    (): Record<string, unknown> => ({
      tieuDe: tieuDe.trim(),
      loaiBanTin: maLoai,
      mucDoUuTien,
      loaiLinhVuc,
      noiDungTomTat: noiDungTomTat.trim() || tieuDe.trim(),
      diaBanTao,
      diaBanChiaSe,
      ...(laAnhVideo ? { vungPhat } : {}),
      // Giữ thời lượng đã có (video tải lên kèm thời lượng) — PUT không gửi thì máy chủ xoá.
      ...(banTin.duLieu?.thoiLuong ? { thoiLuong: banTin.duLieu.thoiLuong } : {}),
    }),
    [
      tieuDe,
      maLoai,
      mucDoUuTien,
      loaiLinhVuc,
      noiDungTomTat,
      diaBanTao,
      diaBanChiaSe,
      laAnhVideo,
      vungPhat,
      banTin.duLieu,
    ],
  );

  const luuNhap = useCallback(async (): Promise<void> => {
    if (!id) return;
    datDangLuu(true);
    try {
      await goi.sua(id, thanGui());
      datLuuLucCuoi(gioHienTai());
      datCoThayDoi(false);
    } finally {
      datDangLuu(false);
    }
  }, [id, goi, thanGui]);

  // Yêu cầu bắt buộc 1: tự lưu nháp mỗi 30 giây khi có thay đổi (khi bản tin đã được tạo).
  useEffect(() => {
    const h = setInterval(() => {
      if (coThayDoi && id) void luuNhap();
    }, CHU_KY_TU_LUU_MS);
    return () => clearInterval(h);
  }, [coThayDoi, luuNhap, id]);

  // Yêu cầu bắt buộc 2: cảnh báo rời trang khi còn thay đổi chưa lưu.
  useEffect(() => {
    const nghe = (e: BeforeUnloadEvent): void => {
      if (!coThayDoi) return;
      e.preventDefault();
      e.returnValue = vi.bienTap.canhBaoRoiTrang;
    };
    window.addEventListener('beforeunload', nghe);
    return () => window.removeEventListener('beforeunload', nghe);
  }, [coThayDoi]);

  const doiTruong =
    <T,>(dat: (v: T) => void) =>
    (v: T): void => {
      dat(v);
      datCoThayDoi(true);
    };

  /* ------------------------------- Cấu tạo âm thanh ------------------------------- */

  const themDoanTuTep = async (tep: File): Promise<void> => {
    if (!id) return;
    if (!DINH_DANG_CHO_PHEP.includes(tep.type)) {
      datThongBaoDoan(vi.bienTap.dinhDangSai);
      return;
    }
    const taiLen = await khoGoi.taiLen(tep, { ten: tep.name });
    await goi.doanAmThanh.them(id, { loaiNguon: 'kho', audioAssetId: taiLen.id });
    doan.taiLai();
  };

  const xoaDoan = async (segId: string): Promise<void> => {
    if (!id) return;
    await goi.doanAmThanh.xoa(id, segId);
    doan.taiLai();
  };

  const doiChoDoan = async (chiSo: number, huong: -1 | 1): Promise<void> => {
    if (!id || !doan.duLieu) return;
    const moi = [...doan.duLieu];
    const dich = chiSo + huong;
    if (dich < 0 || dich >= moi.length) return;
    const tam = moi[chiSo] as DongDoanAmThanh;
    moi[chiSo] = moi[dich] as DongDoanAmThanh;
    moi[dich] = tam;
    await goi.doanAmThanh.doiThuTu(
      id,
      moi.map((d) => d.id),
    );
    doan.taiLai();
  };

  const [dangKeoSo, datDangKeoSo] = useState<number | null>(null);
  const thaVaoDong = async (dichSo: number): Promise<void> => {
    if (!id || dangKeoSo === null || dangKeoSo === dichSo || !doan.duLieu) return;
    const moi = [...doan.duLieu];
    const [phanTu] = moi.splice(dangKeoSo, 1);
    moi.splice(dichSo, 0, phanTu as DongDoanAmThanh);
    await goi.doanAmThanh.doiThuTu(
      id,
      moi.map((d) => d.id),
    );
    datDangKeoSo(null);
    doan.taiLai();
  };

  const thoiLuongTong = (doan.duLieu ?? []).reduce((tong, d) => tong + (d.thoiLuongGiay ?? 0), 0);

  const [loiGuiDuyet, datLoiGuiDuyet] = useState<string | null>(null);

  const guiDuyet = async (): Promise<void> => {
    if (!id) return;
    datDangGuiDuyet(true);
    datLoiGuiDuyet(null);
    try {
      await luuNhap();
      await goi.trinhDuyet(id);
      dieuHuong('/ban-tin');
    } catch (e: unknown) {
      datLoiGuiDuyet(e instanceof Error ? e.message : 'Không gửi duyệt được');
    } finally {
      datDangGuiDuyet(false);
    }
  };

  // Âm thanh: cần ít nhất một đoạn; hình ảnh/video: cần đã tải tệp.
  const guiDuyetBiVoHieu = laAnhVideo ? tenTep === null : (doan.duLieu ?? []).length === 0;

  /* ------------------------ Ba bước "Tạo bản tin — Tải tệp — Hoàn thành" ------------------------ */
  const [buoc, datBuoc] = useState<1 | 2 | 3>(1);
  const [loiTen, datLoiTen] = useState(false);
  const [loiLuu, datLoiLuu] = useState<string | null>(null);
  const [loiBuoc2, datLoiBuoc2] = useState<string | null>(null);
  const [trangThaiSau, datTrangThaiSau] = useState<DongBanTin['trangThai'] | null>(null);
  const [dangTrinh, datDangTrinh] = useState(false);
  const [loiTrinh, datLoiTrinh] = useState<string | null>(null);

  const hoanThanhBuoc1 = async (): Promise<void> => {
    if (!tieuDe.trim()) {
      datLoiTen(true);
      return;
    }
    datLoiTen(false);
    datLoiLuu(null);
    datDangLuu(true);
    try {
      if (id) {
        await goi.sua(id, thanGui());
        datLuuLucCuoi(gioHienTai());
      } else {
        const kq = await goi.tao(thanGui());
        // Không nạp đè biểu mẫu bằng chi tiết vừa tạo (giá trị đã đúng như người dùng nhập).
        daNapRef.current = true;
        datIdThat(kq.id);
      }
      datCoThayDoi(false);
      datBuoc(2);
    } catch (e: unknown) {
      datLoiLuu(dienVao(t.soan.loiLuu, { loi: e instanceof Error ? e.message : '' }));
    } finally {
      datDangLuu(false);
    }
  };

  const sangBuoc3 = (): void => {
    if (laAnhVideo && tenTep === null) {
      datLoiBuoc2(t.tep.canTaiTep);
      return;
    }
    datLoiBuoc2(null);
    datBuoc(3);
  };

  const trinhNgay = async (): Promise<void> => {
    if (!id) return;
    datDangTrinh(true);
    datLoiTrinh(null);
    try {
      const kq = await goi.trinhDuyet(id);
      datTrangThaiSau((kq.trangThai as DongBanTin['trangThai']) || 'chua_duyet');
    } catch (e: unknown) {
      datLoiTrinh(dienVao(t.soan.loiTrinh, { loi: e instanceof Error ? e.message : '' }));
    } finally {
      datDangTrinh(false);
    }
  };

  const nhanLoaiNoiDung =
    (loaiNoiDung ? t.loaiNoiDungNhan[loaiNoiDung as keyof typeof t.loaiNoiDungNhan] : undefined) ??
    (banTin.duLieu
      ? t.loaiBanTinNhan[banTin.duLieu.loaiBanTin as keyof typeof t.loaiBanTinNhan]
      : undefined) ??
    t.loaiNoiDungNhan['tai-file'];

  const trangThaiHienTai = trangThaiSau ?? banTin.duLieu?.trangThai ?? 'tao_moi';
  // Bản tin mới (chưa có id) hiện biểu mẫu ngay; đã có id thì chờ tải chi tiết.
  const coBieuMau = !id || (banTin.trangThai === 'co-du-lieu' && (daNap || idThat !== null));

  return (
    <div className="man-hinh" data-man-hinh="MH-12">
      <h1 className="chi-cho-doc-man-hinh">{laTaoMoi ? t.soan.taoBanTin : vi.bienTap.tieuDeMoi}</h1>
      <BuocSoanBanTin buoc={buoc} />

      {id && banTin.trangThai === 'dang-tai' ? (
        <KhungXuong soDong={6} soCot={2} taiLau={banTin.taiLau} />
      ) : null}
      {id && banTin.trangThai === 'loi' && banTin.loi ? (
        <TrangThaiLoi loi={banTin.loi} onThuLai={banTin.taiLai} />
      ) : null}

      {coBieuMau && buoc === 3 && id ? (
        <HoanThanhBanTin
          laTaoMoi={laTaoMoi}
          ten={tieuDe}
          trangThai={trangThaiHienTai}
          banTinId={id}
          trinhDuoc={!guiDuyetBiVoHieu && phien.coQuyen('bulletin.submit')}
          dangTrinh={dangTrinh}
          loiTrinh={loiTrinh}
          ghiChu={guiDuyetBiVoHieu ? (laAnhVideo ? t.soan.trinhCanTep : t.soan.trinhCanDoan) : undefined}
          onTrinh={() => void trinhNgay()}
        />
      ) : null}

      {coBieuMau && buoc === 1 ? (
        <>
          <p className="nd-loai-noi-dung" data-vung="loai-noi-dung">
            {t.soan.loaiNoiDung}: <b>{nhanLoaiNoiDung}</b>
          </p>
          <div className="nd-form nd-form--mot-cot" data-vung="bien-tap-buoc-1">
            <section className="nd-panel bien-tap__cot-trai" aria-label={vi.bienTap.tieuDeMoi}>
              <TruongSoan nhan={t.soan.tenBanTin} htmlFor="bt-tieu-de" batBuoc>
                <input
                  id="bt-tieu-de"
                  placeholder={t.soan.tenBanTinGoiY}
                  maxLength={100}
                  required
                  aria-invalid={loiTen || undefined}
                  aria-describedby={loiTen ? 'bt-tieu-de-loi' : undefined}
                  value={tieuDe}
                  onChange={(e) => doiTruong<string>(datTieuDe)(e.target.value)}
                />
                {loiTen ? (
                  <span id="bt-tieu-de-loi" className="nd-loi" role="alert">
                    {t.soan.tenBatBuoc}
                  </span>
                ) : null}
              </TruongSoan>

              <TruongSoan nhan={t.soan.quyTrinh} htmlFor="bt-quy-trinh" chuaCoApi>
                <select id="bt-quy-trinh" disabled value="" onChange={() => undefined}>
                  <option value="">{t.soan.quyTrinhMacDinh}</option>
                </select>
              </TruongSoan>

              <div className="nd-g2">
                <TruongSoan nhan={t.soan.linhVucCap1} htmlFor="bt-linh-vuc">
                  <select
                    id="bt-linh-vuc"
                    value={loaiLinhVuc}
                    onChange={(e) => doiTruong<string>(datLoaiLinhVuc)(e.target.value)}
                  >
                    {(Object.keys(vi.banTin.linhVucNhan) as (keyof typeof vi.banTin.linhVucNhan)[]).map(
                      (k) => (
                        <option key={k} value={k}>
                          {vi.banTin.linhVucNhan[k]}
                        </option>
                      ),
                    )}
                  </select>
                </TruongSoan>
                <TruongSoan nhan={t.soan.linhVucCap2} htmlFor="bt-linh-vuc-2" chuaCoApi>
                  <select id="bt-linh-vuc-2" disabled value="" onChange={() => undefined}>
                    <option value="">{t.soan.chonLinhVucCap2}</option>
                  </select>
                </TruongSoan>
              </div>

              <div className="nd-g2">
                <TruongSoan nhan={t.soan.mucDoUuTien} htmlFor="bt-uu-tien">
                  <select
                    id="bt-uu-tien"
                    value={mucDoUuTien}
                    onChange={(e) => doiTruong<string>(datMucDoUuTien)(e.target.value)}
                  >
                    {Object.entries(t.uuTienNhan).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </TruongSoan>
                <TruongSoan nhan={t.soan.nguonTin} htmlFor="bt-nguon-tin">
                  <input
                    id="bt-nguon-tin"
                    disabled
                    value={banTin.duLieu?.nguonTin ?? phien.nguonIdHienTai ?? ''}
                    readOnly
                  />
                </TruongSoan>
              </div>

              <div className="nd-g2" data-vung="hang-loai-ban-tin">
                <TruongSoan nhan={t.soan.loaiBanTin} htmlFor="bt-loai-ban-tin" batBuoc>
                  <select
                    id="bt-loai-ban-tin"
                    disabled={!choChonLoai}
                    value={nhomLoai}
                    onChange={(e) => doiTruong<NhomLoaiBanTin>(datNhomLoai)(e.target.value as NhomLoaiBanTin)}
                  >
                    {DS_NHOM_LOAI_BAN_TIN.map((k) => (
                      <option key={k} value={k}>
                        {t.nhomLoaiNhan[k]}
                      </option>
                    ))}
                  </select>
                  <span className="truong__mo-ta">{t.soan.loaiBanTinGhiChu}</span>
                </TruongSoan>
                <TruongSoan nhan={t.soan.vungPhat} htmlFor="bt-vung-phat">
                  <select
                    id="bt-vung-phat"
                    disabled={!laAnhVideo}
                    value={laAnhVideo ? vungPhat : ''}
                    onChange={(e) => doiTruong<VungPhat>(datVungPhat)(e.target.value as VungPhat)}
                  >
                    {laAnhVideo ? null : <option value="">{t.soan.vungPhatMacDinh}</option>}
                    {DS_VUNG_PHAT.map((v) => (
                      <option key={v} value={v}>
                        {viBangTin.vungPhat[v]}
                      </option>
                    ))}
                  </select>
                  <span className="truong__mo-ta">{t.soan.vungPhatGhiChu}</span>
                </TruongSoan>
              </div>

              <div className="nd-truong">
                <span id="bt-dia-ban-tao-nhan" className="nd-truong__nhan">
                  {vi.bienTap.nhanDiaBanTao}
                </span>
                <p
                  className="nd-chi-doc"
                  data-vung="dia-ban-tao-chi-doc"
                  aria-labelledby="bt-dia-ban-tao-nhan"
                >
                  {tenDiaBanTao ?? (diaBanTao || vi.banTin.chuaCo)}
                </p>
              </div>

              {/* Chia sẻ là việc của tỉnh (bản tin tỉnh xuống xã); tài khoản xã không chia sẻ ra ngoài xã. */}
              {phien.khoaDiaBan ? null : (
                <TruongDiaBanChiaSe
                  tienTo="bt"
                  giaTri={diaBanChiaSe}
                  onDoi={(ds) => {
                    datDiaBanChiaSe(ds);
                    datCoThayDoi(true);
                  }}
                />
              )}

              <TruongSoan nhan={t.soan.moTa} htmlFor="bt-noi-dung">
                <textarea
                  id="bt-noi-dung"
                  rows={4}
                  maxLength={300}
                  value={noiDungTomTat}
                  onChange={(e) => doiTruong<string>(datNoiDungTomTat)(e.target.value)}
                />
              </TruongSoan>
            </section>
          </div>
        </>
      ) : null}

      {coBieuMau && buoc === 2 && id && laAnhVideo ? (
        <KhuTaiTep
          id={id}
          loai={maLoai === '4' ? '4' : '2'}
          tieuDe={tieuDe}
          tenTep={tenTep}
          api={goiTep}
          onDaTai={datTenTep}
        />
      ) : null}

      {coBieuMau && buoc === 2 && id && !laAnhVideo ? (
        <section className="nd-panel bien-tap__cot-phai" aria-label={vi.bienTap.cauTaoAmThanh}>
          <h2 className="nd-tieu-de-phu">{vi.bienTap.cauTaoAmThanh}</h2>
          <p data-vung="tong-thoi-luong">
            {vi.bienTap.tongThoiLuong}: {Math.round(thoiLuongTong)}s
          </p>
          <p className="bien-tap__ghi-chu">{vi.bienTap.chuanHoaGhiChu}</p>

          {doan.trangThai === 'dang-tai' ? <KhungXuong soDong={3} soCot={1} /> : null}
          {thongBaoDoan ? (
            <p role="alert" data-vung="thong-bao-doan">
              {thongBaoDoan}
            </p>
          ) : null}

          {(doan.duLieu ?? []).length === 0 && doan.trangThai === 'co-du-lieu' ? (
            <p>{vi.bienTap.doanRong}</p>
          ) : null}

          <ol className="bien-tap__doan" data-vung="danh-sach-doan">
            {(doan.duLieu ?? []).map((d, i) => (
              <li
                key={d.id}
                draggable
                data-thu-tu={i + 1}
                onDragStart={() => datDangKeoSo(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => void thaVaoDong(i)}
              >
                <span className="bien-tap__doan-tay-cam" aria-hidden="true">
                  ::
                </span>
                <span>
                  {i + 1}. {d.ten ?? d.loaiNguon} {d.thoiLuongGiay ? `${d.thoiLuongGiay}s` : ''}
                </span>
                <button
                  type="button"
                  className="nut nut--nho"
                  aria-label={`${vi.bienTap.diChuyenLen}: ${i + 1}`}
                  disabled={i === 0}
                  onClick={() => void doiChoDoan(i, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="nut nut--nho"
                  aria-label={`${vi.bienTap.diChuyenXuong}: ${i + 1}`}
                  disabled={i === (doan.duLieu?.length ?? 0) - 1}
                  onClick={() => void doiChoDoan(i, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="nut nut--nho"
                  aria-label={`${vi.bienTap.xoaDoan}: ${i + 1}`}
                  onClick={() => void xoaDoan(d.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ol>

          <div className="bien-tap__them-doan">
            <label htmlFor="bt-tep-am-thanh" className="nut">
              {vi.bienTap.themTuTep}
            </label>
            <input
              id="bt-tep-am-thanh"
              type="file"
              accept="audio/mpeg,audio/wav,.mp3,.wav"
              className="chi-cho-doc-man-hinh"
              onChange={(e) => {
                const tep = e.target.files?.[0];
                if (tep) void themDoanTuTep(tep);
                e.target.value = '';
              }}
            />
            <Link className="nut" to={`/ban-tin/tao-tu-van-ban?banTinId=${id}`}>
              {vi.bienTap.themTuTts}
            </Link>
          </div>

          {(doan.duLieu ?? []).length > 0 ? (
            <TrinhPhatAmThanh
              nhan={vi.bienTap.ngheThuToanBo}
              url={(doan.duLieu?.[0] as DongDoanAmThanh | undefined)?.urlNgheThu ?? undefined}
            />
          ) : null}
        </section>
      ) : null}

      {buoc === 1 && coBieuMau ? (
        <div className="nd-chan-form" data-vung="thanh-hanh-dong">
          <p className="nd-chan-form__ghi bien-tap__luu-nhap" data-vung="luu-nhap">
            {dangLuu
              ? vi.bienTap.dangLuu
              : luuLucCuoi
                ? dienVao(vi.bienTap.daLuuNhapLuc, { luc: luuLucCuoi })
                : ''}
          </p>
          {loiLuu ? (
            <p role="alert" className="nd-loi" data-vung="loi-luu">
              {loiLuu}
            </p>
          ) : null}
          <Link to={laTaoMoi ? '/ban-tin/chon-loai' : '/ban-tin'} className="nut nd-nut-quay-lai">
            {t.soan.quayLai}
          </Link>
          {id ? (
            <button type="button" className="nut" onClick={() => void luuNhap()} disabled={dangLuu}>
              {vi.bienTap.luuNhap}
            </button>
          ) : null}
          <button
            type="button"
            className="nut nut--chinh"
            data-hanh-dong="hoan-thanh-buoc-1"
            disabled={dangLuu}
            onClick={() => void hoanThanhBuoc1()}
          >
            {id ? t.soan.capNhat : t.soan.taoBanTin}
          </button>
        </div>
      ) : null}

      {buoc === 2 && coBieuMau ? (
        <div className="nd-chan-form" data-vung="thanh-hanh-dong-buoc-2">
          {loiGuiDuyet ? (
            <p role="alert" className="nd-loi" data-vung="loi-gui-duyet">
              {loiGuiDuyet}
            </p>
          ) : null}
          {loiBuoc2 ? (
            <p role="alert" className="nd-loi" data-vung="loi-buoc-2">
              {loiBuoc2}
            </p>
          ) : null}
          <button type="button" className="nut nd-nut-quay-lai" onClick={() => datBuoc(1)}>
            {t.soan.quayLai}
          </button>
          <button
            type="button"
            className="nut"
            data-hanh-dong="gui-duyet"
            disabled={guiDuyetBiVoHieu || dangGuiDuyet}
            aria-describedby="bt-dieu-kien-gui-duyet"
            onClick={() => void guiDuyet()}
          >
            {dangGuiDuyet ? vi.bienTap.dangGhepAmThanh : vi.bienTap.guiDuyet}
          </button>
          <p id="bt-dieu-kien-gui-duyet" className="chi-cho-doc-man-hinh">
            {laAnhVideo ? t.soan.trinhCanTep : vi.bienTap.dieuKienGuiDuyet}
          </p>
          <button type="button" className="nut nut--chinh" data-hanh-dong="sang-buoc-3" onClick={sangBuoc3}>
            {t.soan.tiepTuc}
          </button>
        </div>
      ) : null}
    </div>
  );
}
